<template>
  <BaseLayout>
    <div class="page-single position-relative">
      <b-container
        fluid
      >
        <b-row class="justify-content-center">
          <b-col class="col-account-setup">
            <div class="text-center mb-6">
              <img
                src="/images/smartmatch-logo.png"
                class="h-6"
                alt="logo"
              >
            </div>

            <b-form
              class="card"
              @submit.prevent="confirm"
            >
              <div class="card-body p-6">
                <div class="card-title">
                  <p>Finish Setting up your Account</p>
                  <small class="card-black-subtitle">Create a password to finish setting up your account, and gain access <br> to shared showcases and reporting.</small>
                </div>
                <b-form-group
                  id="password-group-1"
                  label="Create Password*"
                  label-for="new-password-input"
                  label-class="form-label"
                >
                  <b-form-input
                    id="new-password-input"
                    v-model="newPassword"
                    type="password"
                    required
                    placeholder="Password"
                    autocomplete="off"
                    data-test="new-password-input"
                    @blur.native="onBlurPassword"
                    :class="[newPassword && !passwordMeetsRequirements ? 'is-invalid' : '']"
                  />
                  <p
                    v-if="newPassword && !passwordMeetsRequirements"
                    class="invalid"
                  >
                    Supplied password does not meet the requirements
                  </p>
                </b-form-group>
                <b-form-group
                  id="password-group-2"
                  label="Repeat Password*"
                  label-for="repeat-password-input"
                  label-class="form-label"
                >
                  <b-form-input
                    id="repeat-password-input"
                    v-model="repeatPassword"
                    type="password"
                    required
                    placeholder="Password"
                    autocomplete="off"
                    data-test="repeat-password-input"
                    @blur.native="passwordsMatch = newPassword === repeatPassword"
                    :class="[repeatPassword && !passwordsMatch ? 'is-invalid' : '']"
                  />
                  <p
                    v-if="repeatPassword && !passwordsMatch"
                    class="invalid"
                  >
                    Passwords do not match
                  </p>
                </b-form-group>
                <b-alert
                  show
                  variant="info"
                  class="multiline"
                >
                  <small>
                    Password requirements:
                    <ul class="mb-0 pl-3">
                      <li>Minimum length of 8 characters</li>
                      <li>At least 1 number</li>
                      <li>At least 1 special character from this set:<br>
                        ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \ , > ' : ; | _ ~ `
                      </li>
                      <li>At least 1 uppercase letter</li>
                      <li>At least 1 lowercase letter</li>
                    </ul>
                  </small>
                </b-alert>
                <div class="form-footer">
                  <button
                    type="submit"
                    class="btn btn-primary btn-block"
                    :disabled="!passwordMeetsRequirements || newPassword !== repeatPassword"
                    data-test="confirm-submit"
                    :class="{ 'btn-loading': isLoading }"
                  >
                    Create Account
                  </button>
                </div>
              </div>
            </b-form>
            <div class="text-center text-muted mt-4">
              Need support? Contact our team <a
                href="mailto:support@smartology.net"
                target="_blank"
              >support@smartology.net</a>
            </div>
          </b-col>
        </b-row>
        <div
          class="header-brand d-flex justify-content-center mb-5 mr-0 smartology-logo-footer"
          style="z-index: 0;"
        >
          <a
            target="_blank"
            href="https://www.smartology.net/"
          >
            <img
              src="/images/logo.png"
              class="h-7 header-brand-img mr-0"
              alt="Smartology"
            >
          </a>
        </div>
      </b-container>
    </div>
  </BaseLayout>
</template>

<script>
import signUtils from '@/core/signUtils';
import { mapActions, mapState, mapMutations } from 'vuex';
import * as types from '@/store/types';
import * as authenticationTypes from '@/modules/authentication/store/types';
import * as accountTypes from '@/modules/account/store/types';
import { Auth } from 'aws-amplify';
import { noop } from 'lodash';

export default {
  name: 'FinishAccountSetup',
  data() {
    return {
      tempPassword: '',
      newPassword: '',
      repeatPassword: '',
      isLoading: false,
      passwordsMatch: true,
      passwordMeetsRequirements: true,
    };
  },
  computed: {
    ...mapState('authentication', {
      user: 'user',
    }),
  },
  methods: {
    ...mapActions('authentication', [
      authenticationTypes.SIGN_IN,
      authenticationTypes.SIGN_OUT,
      authenticationTypes.CONFIRM_SIGN_UP,
      authenticationTypes.CHANGE_PASSWORD,
    ]),
    ...mapActions('account', [
      accountTypes.FETCH_ACCOUNT,
      accountTypes.UPDATE_ACCOUNT,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    onBlurPassword() {
      this.passwordMeetsRequirements = signUtils.doesPasswordMeetRequirements(this.newPassword);
      if (this.repeatPassword) this.passwordsMatch = this.newPassword === this.repeatPassword;
    },
    async confirm() {
      if (this.newPassword !== this.repeatPassword) {
        return;
      }
      this.isLoading = true;
      try {
        await this.CHANGE_PASSWORD({
          user: this.user.data,
          oldPassword: this.tempPassword,
          newPassword: this.newPassword,
        });
        this.redirect();
      } catch (error) {
        this.isLoading = false;
      }
    },
    redirect() {
      if (this.$route.query.redirect) {
        this.$router.replace(this.$route.query.redirect);
      } else {
        this.$router.replace('/dashboard');
      }
    },
    async confirmSignUp(accountEmail, confirmationCode) {
      try {
        await this.CONFIRM_SIGN_UP({
          email: accountEmail,
          code: confirmationCode,
        });
      } catch (error) {
        noop();
      }
    },
    async loginUserWithTempPassword(accountEmail, authCode) {
      try {
        await this.SIGN_IN({
          code: authCode,
          email: accountEmail,
          password: this.tempPassword,
          ignoreError: true,
        });
        await this.FETCH_ACCOUNT();
      } catch (error) {
        this.redirect();
      }
    },
  },

  async created() {
    const accountEmail = this.$route.query.email;
    const authCode = this.$route.query.code;
    const { confirmationCode } = this.$route.query;
    const isAuthCodeValid = authCode && authCode.length >= 32 && authCode.split('-').length === 5;

    if (!accountEmail || !confirmationCode || !isAuthCodeValid) {
      this.redirect();
    } else {
      const authenticatedUser = await Auth.currentAuthenticatedUser().catch(() => noop());
      const userAccount = authenticatedUser ? await this.FETCH_ACCOUNT() : undefined;
      if (userAccount && userAccount.email === accountEmail) {
        this.redirect();
      } else {
        if (userAccount) {
          await this.SIGN_OUT();
        }
        const reverseString = (string) => string.split('').reverse().join('');
        this.tempPassword = `${reverseString(authCode.split('-')[4])}-Sm1`;
        await this.confirmSignUp(accountEmail, confirmationCode);
        await this.loginUserWithTempPassword(accountEmail, authCode);
      }
    }
  },
};
</script>
